import Component from '@glimmer/component'
import { service } from '@ember/service'
import { colors } from '@colors'
import { formatNumber } from 'teacher-ui-client/utils/giraffe/formatter'
import { t } from 'ember-intl'
import type GradeSetsService from 'teacher-ui-client/services/grade-sets'
import { SeriesBar } from 'teacher-ui-client/components/giraffe/primitives/series-bar'
import type { SeriesBarOptions } from 'highcharts'

export interface CourseProgressByGradeData {
  grades: {
    grade_position: number
    lesson_count: number
    student_count: number
  }[]
  student_count: number
}

interface Args {
  data: CourseProgressByGradeData
  precinct: 'lessons' | 'spelling' | 'reading' | 'my_lessons'
  product: 'maths' | 're' | 'rex'
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

export class DashboardCourseProgress extends Component<Signature> {
  @service gradeSets!: GradeSetsService

  readonly singleGradeChartMargin: (number | null)[] = [55, null, 104, null]

  get chartBars(): SeriesBarOptions[] {
    const byGrade = this.args.data.grades
    return [
      {
        data: byGrade.map((grade) => {
          if (grade.student_count === 0) {
            return 0
          }
          return Math.round(grade.lesson_count / grade.student_count)
        }),
        color: this.color,
        type: 'bar' as const,
      },
    ]
  }

  get categories(): string[] {
    return this.args.data.grades.map((grade) => this.gradeSets.findByPosition(grade.grade_position).name ?? '')
  }

  get color(): string {
    const { product, precinct } = this.args
    switch (`${product}.${precinct}`) {
      case 'maths.lessons':
        return colors.forestGreen300
      case 're.spelling':
      case 'rex.spelling':
        return colors.grapeyGreen300
      case 're.reading':
      case 'rex.my_lessons':
        return colors.oceanyBlue300
      default:
        return colors.oceanyBlue300
    }
  }

  get subtitleKey(): string {
    return this.singleGrade
      ? 'reporting.dashboardSubtitles.singleGrade.courseProgress'
      : 'reporting.dashboardSubtitles.multiGrade'
  }

  get singleGrade(): boolean {
    return this.args.data.grades.length === 1
  }

  get studentCount(): string {
    const count = this.args.data.student_count
    return formatNumber(count, 2)
  }

  <template>
    <div data-test-giraffe-dashboard-course-progress class="flex h-full flex-col" ...attributes>
      <div class="flex justify-between">
        <p class="text-dusty-black-500 mb-3 pr-[5px] pl-0 text-xs">
          {{t this.subtitleKey}}
        </p>
        <p data-test-course-progress-subtitle class="text-dusty-black-500 mb-3 pr-0 pl-[5px] text-left text-xs">
          {{t "reporting.dashboardSubtitles.studentCount" count=this.studentCount}}
        </p>
      </div>
      <div class="relative grow">
        <div class="absolute inset-0">
          {{#if this.singleGrade}}
            <SeriesBar
              @data={{this.chartBars}}
              @categories={{this.categories}}
              @xAxisLabel={{t "grade.label"}}
              @yAxisLabel={{t "myLessons"}}
              @groupPadding={{0}}
              @chartMargin={{this.singleGradeChartMargin}}
              @yAxisOffset={{25}}
              @yAxisLineWidth={{1}}
              @yAxisGridLineWidth={{0}}
            />
          {{else}}
            <SeriesBar
              @data={{this.chartBars}}
              @categories={{this.categories}}
              @xAxisLabel={{t "grade.label"}}
              @yAxisLabel={{t "myLessons"}}
            />
          {{/if}}
        </div>
      </div>
    </div>
  </template>
}

export default DashboardCourseProgress
